import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  FileText,
  Send,
} from 'lucide-react';
import { ReportPreviewDialog } from '../components/reports/ReportPreviewDialog';
import { SendReportDialog } from '../components/reports/SendReportDialog';
import ReportCard from '../components/reports/ReportCard';
import EditNotesDialog from '../components/reports/EditNotesDialog';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [sendingReport, setSendingReport] = useState(null);
  const [emailList, setEmailList] = useState('');
  const [clientContacts, setClientContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [contactsLoading, setContactsLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    period_start: '',
    period_end: '',
    report_type: 'monthly',
    summary: ''
  });
  const [editFormData, setEditFormData] = useState({
    consultant_notes: '',
    technical_actions: '',
    leads_count: 0
  });
  const [periodLeadCount, setPeriodLeadCount] = useState(null);
  const [loadingLeadCount, setLoadingLeadCount] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');

  // Load active branding to display sender email in Send dialog
  useEffect(() => {
    axios.get(`${API}/settings/branding/active`, { withCredentials: true })
      .then(({ data }) => setSenderEmail(data?.from_email || data?.sender_email || ''))
      .catch(() => {});
  }, []);

  // Fetch lead count whenever client + dates are valid (debounced)
  useEffect(() => {
    if (!dialogOpen) return;
    const { client_id, period_start, period_end } = formData;
    if (!client_id || !period_start || !period_end) {
      setPeriodLeadCount(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoadingLeadCount(true);
      try {
        const { data } = await axios.get(
          `${API}/leads/count-for-period?client_id=${client_id}&start=${period_start}&end=${period_end}`,
          { withCredentials: true }
        );
        setPeriodLeadCount(data);
      } catch {
        setPeriodLeadCount(null);
      } finally {
        setLoadingLeadCount(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [dialogOpen, formData.client_id, formData.period_start, formData.period_end]);

  const insertLeadsLine = () => {
    if (!periodLeadCount) return;
    const start = format(new Date(formData.period_start), 'dd/MM/yyyy');
    const end = format(new Date(formData.period_end), 'dd/MM/yyyy');
    const line = `Nombre de leads reçus sur la période du ${start} au ${end} : ${periodLeadCount.total}`;
    setFormData(f => ({
      ...f,
      summary: f.summary ? `${line}\n\n${f.summary}` : line,
    }));
    toast.success('Ligne insérée dans le résumé');
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteReport = async (report) => {
    if (!window.confirm(`Supprimer le rapport "${report.title}" ?`)) return;
    try {
      await axios.delete(`${API}/reports/${report.id}`, { withCredentials: true });
      toast.success('Rapport supprimé');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de supprimer ce rapport');
    }
  };

  const fetchData = async () => {
    try {
      const [reportsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/reports`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true })
      ]);
      setReports(reportsRes.data);
      setClients(clientsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reports`, {
        ...formData,
        period_start: new Date(formData.period_start).toISOString(),
        period_end: new Date(formData.period_end).toISOString()
      }, { withCredentials: true });
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handlePublish = async (reportId) => {
    try {
      await axios.put(`${API}/reports/${reportId}/publish`, {}, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      title: '',
      period_start: '',
      period_end: '',
      report_type: 'monthly',
      summary: ''
    });
  };

  const handlePreview = async (reportId) => {
    try {
      const [reportRes, pdfDataRes] = await Promise.all([
        axios.get(`${API}/reports/${reportId}`, { withCredentials: true }),
        axios.get(`${API}/reports/${reportId}/pdf-data`, { withCredentials: true })
      ]);
      setPreviewReport(reportRes.data);
      setPdfData(pdfDataRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handleEditClick = (report) => {
    setEditingReport(report);
    setEditFormData({
      consultant_notes: report.consultant_notes || '',
      technical_actions: report.technical_actions || '',
      leads_count: report.metrics?.leads_count || 0
    });
    setEditDialogOpen(true);
  };

  const handleSaveConsultantNotes = async () => {
    try {
      await axios.put(`${API}/reports/${editingReport.id}/consultant-notes`, editFormData, { withCredentials: true });
      setEditDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handleSendClick = async (report) => {
    setSendingReport(report);
    setEmailList('');
    setSelectedContactIds(new Set());
    setSendDialogOpen(true);
    
    // Load contacts for this client
    setContactsLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients/${report.client_id}/contacts`, { withCredentials: true });
      setClientContacts(data);
      // Pre-select all contacts
      setSelectedContactIds(new Set(data.map(c => c.id)));
    } catch (e) {
      setClientContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const toggleContact = (contactId) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const toggleAllContacts = () => {
    if (selectedContactIds.size === clientContacts.length) {
      setSelectedContactIds(new Set());
    } else {
      setSelectedContactIds(new Set(clientContacts.map(c => c.id)));
    }
  };

  const handleSendReport = async () => {
    const extraEmails = emailList.split(/[,;\n]/).map(e => e.trim()).filter(e => e);
    const contactIds = Array.from(selectedContactIds);
    
    if (contactIds.length === 0 && extraEmails.length === 0) return;

    try {
      const { data } = await axios.post(`${API}/reports/${sendingReport.id}/send`, {
        emails: extraEmails,
        contact_ids: contactIds
      }, { withCredentials: true });
      setSendDialogOpen(false);
      alert(`${data.message}\nExpéditeur : ${data.sender}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handleExportPDF = (report, pdfData) => {
    const managerSection = pdfData?.manager_section || {};
    const technicalSection = pdfData?.technical_section || {};

    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${report.title}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
          body { font-family: 'IBM Plex Sans', sans-serif; padding: 40px; color: #0A0A0A; }
          h1 { color: #002FA7; margin-bottom: 10px; font-size: 28px; }
          h2 { color: #002FA7; margin-top: 30px; margin-bottom: 15px; font-size: 20px; border-bottom: 2px solid #002FA7; padding-bottom: 5px; }
          h3 { color: #525252; font-size: 16px; margin-top: 20px; }
          .meta { color: #525252; margin-bottom: 30px; font-size: 14px; }
          .manager-box { background: #F5F5F5; padding: 20px; margin: 20px 0; border-left: 4px solid #002FA7; }
          .manager-box h2 { margin-top: 0; border: none; }
          .stat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 15px 0; }
          .stat-item { text-align: center; padding: 15px; background: white; border: 1px solid #E5E5E5; }
          .stat-value { font-size: 32px; font-weight: bold; color: #002FA7; }
          .stat-label { font-size: 12px; color: #737373; text-transform: uppercase; }
          .stat-green { color: #22C55E; }
          .stat-red { color: #EF4444; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #E5E5E5; padding: 10px; text-align: left; font-size: 13px; }
          th { background: #F5F5F5; font-weight: 600; }
          .position { font-family: monospace; font-weight: bold; }
          .technical-notes { background: #FAFAFA; padding: 15px; margin: 15px 0; border: 1px solid #E5E5E5; white-space: pre-wrap; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E5E5; color: #737373; font-size: 12px; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${report.title}</h1>
        <div class="meta">
          <p><strong>Client:</strong> ${report.client_name}</p>
          <p><strong>Période:</strong> ${format(new Date(report.period_start), 'dd MMMM yyyy', { locale: fr })} - ${format(new Date(report.period_end), 'dd MMMM yyyy', { locale: fr })}</p>
          <p><strong>Généré le:</strong> ${format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}</p>
        </div>

        <div class="manager-box">
          <h2>Le Coin du Manager</h2>
          <p>Résumé exécutif des performances SEO du mois</p>
          
          <div class="stat-grid">
            <div class="stat-item">
              <div class="stat-value">${managerSection.leads_this_month || 0}</div>
              <div class="stat-label">Formulaires reçus</div>
            </div>
            <div class="stat-item">
              <div class="stat-value stat-green">+${managerSection.keywords_improved || 0}</div>
              <div class="stat-label">Positions améliorées</div>
            </div>
            <div class="stat-item">
              <div class="stat-value stat-red">-${managerSection.keywords_declined || 0}</div>
              <div class="stat-label">Positions en baisse</div>
            </div>
          </div>

          ${managerSection.priority_keywords?.length > 0 ? `
          <h3>Mots-clés prioritaires</h3>
          <table>
            <tr><th>Mot-clé</th><th>Position actuelle</th><th>Évolution</th></tr>
            ${managerSection.priority_keywords.map(kw => `
              <tr>
                <td>${kw.keyword}</td>
                <td class="position">${kw.current_position || '-'}</td>
                <td class="position" style="color: ${kw.current_position < kw.previous_position ? '#22C55E' : kw.current_position > kw.previous_position ? '#EF4444' : '#737373'}">
                  ${kw.previous_position ? (kw.previous_position - kw.current_position > 0 ? '+' : '') + (kw.previous_position - kw.current_position) : '-'}
                </td>
              </tr>
            `).join('')}
          </table>
          ` : ''}
        </div>

        ${report.summary ? `
        <h2>Résumé</h2>
        <p>${report.summary}</p>
        ` : ''}

        <h2>Actions techniques du mois</h2>
        ${technicalSection.consultant_notes ? `
        <div class="technical-notes">${technicalSection.consultant_notes}</div>
        ` : '<p style="color: #737373;">Aucune note technique ajoutée</p>'}

        <h2>Tous les mots-clés suivis</h2>
        ${technicalSection.all_keywords?.length > 0 ? `
        <table>
          <tr><th>Mot-clé</th><th>Position</th><th>Évolution</th><th>Prioritaire</th></tr>
          ${technicalSection.all_keywords.map(kw => `
            <tr>
              <td>${kw.keyword}</td>
              <td class="position">${kw.current_position || '-'}</td>
              <td class="position" style="color: ${kw.current_position < kw.previous_position ? '#22C55E' : kw.current_position > kw.previous_position ? '#EF4444' : '#737373'}">
                ${kw.previous_position && kw.current_position ? (kw.previous_position - kw.current_position > 0 ? '+' : '') + (kw.previous_position - kw.current_position) : '-'}
              </td>
              <td>${kw.is_priority ? 'Oui' : 'Non'}</td>
            </tr>
          `).join('')}
        </table>
        ` : '<p>Aucun mot-clé suivi</p>'}

        <div class="footer">
          <p>Rapport généré par SEO Agency Platform</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.open();
    printWindow.document.writeln(content);
    printWindow.document.close();
    printWindow.print();
  };

  const reportTypeLabels = {
    monthly: 'Mensuel',
    quarterly: 'Trimestriel',
    annual: 'Annuel'
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Rapports
            </h1>
            <p className="text-neutral-500 mt-1">Générez et gérez vos rapports clients</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#002FA7] hover:bg-[#002277] text-white"
                data-testid="create-report-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un rapport
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Nouveau rapport</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Titre du rapport *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Rapport SEO Janvier 2024"
                    required
                    className="mt-1"
                    data-testid="report-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client">Client *</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                      required
                    >
                      <SelectTrigger className="mt-1" data-testid="report-client-select">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="report_type">Type de rapport</Label>
                    <Select
                      value={formData.report_type}
                      onValueChange={(value) => setFormData({ ...formData, report_type: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="report-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Mensuel</SelectItem>
                        <SelectItem value="quarterly">Trimestriel</SelectItem>
                        <SelectItem value="annual">Annuel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="period_start">Début de période *</Label>
                    <Input
                      id="period_start"
                      type="date"
                      value={formData.period_start}
                      onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="report-start-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="period_end">Fin de période *</Label>
                    <Input
                      id="period_end"
                      type="date"
                      value={formData.period_end}
                      onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="report-end-date-input"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="summary">Résumé exécutif</Label>
                    {formData.client_id && formData.period_start && formData.period_end && (
                      <div className="flex items-center gap-2 text-xs">
                        {loadingLeadCount ? (
                          <span className="text-neutral-400">Chargement…</span>
                        ) : periodLeadCount ? (
                          <>
                            <span className="text-neutral-600">
                              <strong className="text-[#002FA7] font-mono">{periodLeadCount.total}</strong> lead{periodLeadCount.total > 1 ? 's' : ''} sur la période
                            </span>
                            <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs"
                                    onClick={insertLeadsLine} data-testid="insert-leads-btn">
                              + Insérer
                            </Button>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <Textarea
                    id="summary"
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    placeholder="Points clés du rapport..."
                    className="mt-1"
                    rows={4}
                    data-testid="report-summary-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#002FA7] hover:bg-[#002277]"
                    data-testid="save-report-btn"
                  >
                    Créer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reports Grid */}
        {loading ? (
          <div className="p-8 text-center" data-testid="reports-loading">
            <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
          </div>
        ) : reports.length === 0 ? (
          <div className="bg-white border border-neutral-200 p-12 text-center" data-testid="no-reports">
            <FileText className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-500">Aucun rapport créé</p>
            <p className="text-sm text-neutral-400 mt-1">Créez votre premier rapport client</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
            {reports.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                isAdmin={isAdmin}
                onPreview={handlePreview}
                onEditNotes={handleEditClick}
                onSend={handleSendClick}
                onPublish={handlePublish}
                onDelete={handleDeleteReport}
              />
            ))}
          </div>
        )}

        {/* Edit Consultant Notes Dialog */}
        <EditNotesDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          formData={editFormData}
          setFormData={setEditFormData}
          onSave={handleSaveConsultantNotes}
        />

        {/* Send Report Dialog */}
        <SendReportDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          contacts={clientContacts}
          contactsLoading={contactsLoading}
          selectedContactIds={selectedContactIds}
          toggleContact={toggleContact}
          toggleAllContacts={toggleAllContacts}
          emailList={emailList}
          setEmailList={setEmailList}
          onSend={handleSendReport}
          senderEmail={senderEmail}
        />

        {/* Report Preview Dialog */}
        <ReportPreviewDialog
          previewReport={previewReport}
          pdfData={pdfData}
          onClose={() => { setPreviewReport(null); setPdfData(null); }}
          onSave={async (reportId, data) => {
            await axios.put(`${API}/reports/${reportId}/consultant-notes`, data, { withCredentials: true });
            // Refresh pdf data
            const { data: refreshed } = await axios.get(`${API}/reports/${reportId}/pdf-data`, { withCredentials: true });
            setPdfData(refreshed);
          }}
        />
      </div>
    </Layout>
  );
}

